import { CrudForm } from '../shared/CrudPage';
import { couponService } from '@kss/api';
import type { Coupon, CouponCreate, CouponFilter } from '@kss/api';

/**
 * Coupon form, mapped to the LIVE /sales/Coupon schema. The customer-typed
 * code is `couponCode` (NOT the row `code`), the type is `discountType`, and
 * the window is `validFrom`/`validUntil`. A coupon created here is therefore
 * immediately usable by the bill's "Apply coupon" box.
 */

type F = {
  couponCode: string;
  name: string;
  description: string;
  discountType: string;
  discountValue: number;
  validFrom: string;
  validUntil: string;
  maxUses: number;
  isSingleUsePerCustomer: boolean;
  couponStatus: string;
  isActive: boolean;
};

const empty: F = {
  couponCode: '',
  name: '',
  description: '',
  discountType: 'Percentage',
  discountValue: 0,
  validFrom: '',
  validUntil: '',
  maxUses: 0,
  isSingleUsePerCustomer: false,
  couponStatus: 'Active',
  isActive: true,
};

const get = (r: unknown, k: string): unknown => (r as Record<string, unknown>)?.[k];

export default function NewCoupon() {
  return (
    <CrudForm<Coupon, CouponCreate, CouponFilter, F>
      title="coupon"
      service={couponService}
      idKey="couponId"
      basePath="/panel/coupons"
      emptyForm={empty}
      fields={[
        { key: 'couponCode', label: 'Coupon code', required: true, placeholder: 'WELCOME50' },
        { key: 'name', label: 'Name', required: true },
        { key: 'discountType', label: 'Discount type', type: 'select', options: [{ value: 'Percentage', label: 'Percentage %' }, { value: 'Flat', label: 'Flat ₹' }, { value: 'FreeDelivery', label: 'Free delivery' }] },
        { key: 'discountValue', label: 'Discount value', type: 'number' },
        { key: 'validFrom', label: 'Valid from' },
        { key: 'validUntil', label: 'Valid until' },
        { key: 'maxUses', label: 'Max total uses', type: 'number' },
        { key: 'isSingleUsePerCustomer', label: 'One use per customer', type: 'status' },
        { key: 'couponStatus', label: 'Coupon status', type: 'select', options: [{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }, { value: 'Expired', label: 'Expired' }] },
        { key: 'description', label: 'Description' },
        { key: 'isActive', label: 'Status', type: 'status' },
      ]}
      fromEntity={(r) => ({
        couponCode: String(get(r, 'couponCode') ?? get(r, 'code') ?? ''),
        name: String(get(r, 'name') ?? ''),
        description: String(get(r, 'description') ?? ''),
        discountType: String(get(r, 'discountType') ?? 'Percentage'),
        discountValue: Number(get(r, 'discountValue') ?? 0),
        validFrom: String(get(r, 'validFrom') ?? ''),
        validUntil: String(get(r, 'validUntil') ?? ''),
        maxUses: Number(get(r, 'maxUses') ?? 0),
        isSingleUsePerCustomer: !!get(r, 'isSingleUsePerCustomer'),
        couponStatus: String(get(r, 'couponStatus') ?? 'Active'),
        isActive: !!r.isActive,
      })}
      toEntity={(f, id) => ({
        couponId: id,
        // `code` is the row identifier; `couponCode` is what customers type.
        code: f.couponCode ? f.couponCode : undefined,
        couponCode: f.couponCode ? f.couponCode : undefined,
        name: f.name ? f.name : undefined,
        description: f.description ? f.description : undefined,
        discountType: f.discountType ? f.discountType : undefined,
        discountValue: f.discountValue ? Number(f.discountValue) : undefined,
        validFrom: f.validFrom ? f.validFrom : undefined,
        validUntil: f.validUntil ? f.validUntil : undefined,
        maxUses: f.maxUses ? Number(f.maxUses) : undefined,
        isSingleUsePerCustomer: f.isSingleUsePerCustomer,
        couponStatus: f.couponStatus ? f.couponStatus : undefined,
        status: 0,
        isActive: f.isActive,
      } as unknown as Coupon)}
      validate={(f) => {
        if (!f.couponCode.trim()) return 'Coupon code is required';
        if (!f.name.trim()) return 'Name is required';
        if (!/free/i.test(f.discountType) && !(Number(f.discountValue) > 0)) return 'Discount value must be greater than 0';
        return null;
      }}
    />
  );
}
